import React, { useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";

import styles from "./styles.module.scss";
import { useMedia } from 'hooks/useMedia';
import { ITEMS_PER_PAGE_OPTIONS } from 'constants/settings';

const reg = /offset/;

export default function Pagination({
    url,
    count = 0,
    itemsOnPage = 20,
    onItemsPerPageChange,
    showItemsPerPageSelector = false,
}) {
    const { isDesktop } = useMedia();
    const pagesCount = Math.ceil(count / itemsOnPage);
    const navigate = useNavigate();
    const location = useLocation();

    const createHref = useCallback(
        (a) => {
            const params = new URLSearchParams(location.search);
            params.set('offset', String((a - 1) * itemsOnPage));
            return `${location.pathname}?${params.toString()}`;
        },
        [itemsOnPage, location.pathname, location.search],
    );

    const handlePageClick = useCallback(
        ({ selected }) => {
            const params = new URLSearchParams(location.search);
            params.set('offset', String(selected * itemsOnPage));
            navigate(`${location.pathname}?${params.toString()}`);
            window.scrollTo(0, 0);
        },
        [location.pathname, location.search, navigate, itemsOnPage],
    );

    const initialPage = useMemo(() => {
        if (location.search.split("&").filter((el) => reg.test(el)).length) {
            const currentItemsCount = location.search
                .split("&")
                .filter((el) => reg.test(el))[0]
                .split("=")[1];

            return currentItemsCount / itemsOnPage;
        }

        return 0;
    }, [location.search, itemsOnPage]);

    const getCurrentRange = useMemo(() => {
        const currentPage = initialPage;
        const startItem = currentPage * itemsOnPage + 1;
        const endItem = Math.min((currentPage + 1) * itemsOnPage, count);

        return { startItem, endItem };
    }, [initialPage, itemsOnPage, count]);

    const handleItemsPerPageChange = useCallback(
        (e) => {
            const value = parseInt(e.target.value, 10);
            if (onItemsPerPageChange) {
                onItemsPerPageChange(value);
            }
        },
        [onItemsPerPageChange],
    );

    const pageRangeDisplayed = isDesktop ? 3 : 2
    const marginPagesDisplayed = isDesktop ? 3 : 1;

    return (
      <div className={styles.paginationWrapper}>
        <div className={styles.paginationLeft}>
            <ReactPaginate
                breakLabel="..."
                nextLabel=">"
                previousLabel="<"
                onPageChange={handlePageClick}
                forcePage={initialPage}
                hrefBuilder={createHref}
                pageRangeDisplayed={pageRangeDisplayed}
                pageCount={pagesCount}
                renderOnZeroPageCount={null}
                className={styles.pagination}
                pageClassName={styles.pagination__item}
                previousClassName={styles.pagination__item}
                nextClassName={styles.pagination__item}
                breakClassName={styles.pagination__item}
                pageLinkClassName={styles.pagination__link}
                previousLinkClassName={styles.pagination__link}
                nextLinkClassName={styles.pagination__link}
                breakLinkClassName={styles.pagination__link}
                disabledClassName={styles["pagination__item--disabled"]}
                activeClassName={styles["pagination__item--active"]}
                marginPagesDisplayed={marginPagesDisplayed}
            />

            {count > 0 && (
              <div className={styles.paginationInfo}>
                <span>
                  {getCurrentRange.startItem} - {getCurrentRange.endItem} из {count}
                </span>
              </div>
            )}
        </div>

        {showItemsPerPageSelector && onItemsPerPageChange && (
            <div className={styles.itemsPerPageSelector}>
                <select
                    value={itemsOnPage}
                    onChange={handleItemsPerPageChange}
                    className={styles.itemsPerPageSelect}
                >
                    {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    ))}
                </select>
            </div>
        )}
      </div>
    );
}
